import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Lightbulb, Brain, TrendingUp, TrendingDown, Tag, Heart, Sparkles, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useLanguage } from '@/context/LanguageContext';
import { insightsApi } from '@/lib/api';

const TYPE_CONFIG = {
  mood_trend: { icon: TrendingUp, color: 'cyan' },
  sentiment: { icon: Heart, color: 'rose-400' },
  focus_area: { icon: Tag, color: 'gold' },
  recommendation: { icon: Sparkles, color: 'purple-400' },
};

const TITLE_KEYS = {
  mood_trend: 'Mood Trend',
  sentiment: 'insights_emotional_patterns',
  focus_area: 'Focus Area',
  recommendation: 'Smart Recommendation',
};

export default function InsightsPage() {
  const { t } = useLanguage();
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInsights = async () => {
      try {
        const response = await insightsApi.getAll();
        setInsights(response.data);
      } catch (error) {
        console.error('Failed to fetch insights:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchInsights();
  }, []);

  const getLocalizedTitle = (insight) => {
    const key = TITLE_KEYS[insight.type];
    if (key) {
      const translated = t(key);
      if (translated !== key) return translated;
    }
    return insight.title;
  };

  const getLocalizedDescription = (insight) => {
    if (insight.type === 'sentiment' && insight.data) {
      const { positive, negative, neutral } = insight.data;
      const total = positive + negative + neutral;
      const dominant = positive > negative && positive > neutral
        ? t('insights_positive')
        : negative > positive && negative > neutral
        ? t('insights_negative')
        : t('insights_stable').toLowerCase();
      return `${t('insights_emotional_patterns')}: ${dominant}. ${positive}/${total} ${t('insights_positive_entries')}.`;
    }
    if (insight.type === 'mood_trend' && insight.data) {
      const trendText = insight.data.trend === 'improving' ? t('insights_trending_up')
        : insight.data.trend === 'declining' ? t('insights_trending_down')
        : t('insights_stable');
      return `${trendText}. ${t('analytics_avg_mood')}: ${insight.data.average?.toFixed(1)}/10`;
    }
    if (insight.type === 'focus_area' && insight.data) {
      return `#${insight.data.top_tag} — ${insight.data.count} ${t('insights_mentions')}`;
    }
    return insight.description;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="animate-spin text-cyan" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="insights-page">
      <div>
        <h1 className="font-heading text-3xl text-white mb-2 flex items-center gap-3">
          <Lightbulb className="text-gold" />
          {t('insights_title')}
        </h1>
        <p className="text-slate-400">{t('insights_subtitle')}</p>
      </div>

      {insights.length === 0 ? (
        <Card className="bg-navy-900/40 border-white/5">
          <CardContent className="p-12 text-center">
            <Brain className="mx-auto text-slate-600 mb-4" size={48} />
            <h3 className="text-white text-xl font-medium mb-2">{t('insights_empty_title')}</h3>
            <p className="text-slate-400">{t('insights_empty_desc')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {insights.map((insight, index) => {
            const config = TYPE_CONFIG[insight.type] || { icon: Lightbulb, color: 'cyan' };
            const Icon = config.icon;
            const color = config.color;

            return (
              <motion.div
                key={insight.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className={`bg-navy-900/40 border-white/5 hover:border-${color}/30 transition-colors h-full`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl bg-${color}/10 flex items-center justify-center`}>
                        <Icon className={`text-${color}`} size={20} />
                      </div>
                      <CardTitle className="text-white text-lg">{getLocalizedTitle(insight)}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-300 leading-relaxed">{getLocalizedDescription(insight)}</p>

                    {insight.data && (
                      <div className="mt-4 pt-4 border-t border-white/5">
                        {insight.type === 'mood_trend' && insight.data.trend && (
                          <div className="flex items-center gap-2">
                            {insight.data.trend === 'improving' ? <TrendingUp className="text-green-400" size={16} /> :
                             insight.data.trend === 'declining' ? <TrendingDown className="text-red-400" size={16} /> :
                             <span className="text-yellow-400">→</span>}
                            <span className={`text-sm ${
                              insight.data.trend === 'improving' ? 'text-green-400' :
                              insight.data.trend === 'declining' ? 'text-red-400' : 'text-yellow-400'
                            }`}>
                              {insight.data.trend === 'improving' ? t('insights_trending_up') :
                               insight.data.trend === 'declining' ? t('insights_trending_down') : t('insights_stable')}
                            </span>
                          </div>
                        )}

                        {insight.type === 'sentiment' && (
                          <div className="flex gap-4">
                            <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-green-400" /><span className="text-xs text-slate-400">{insight.data.positive} {t('insights_positive')}</span></div>
                            <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-red-400" /><span className="text-xs text-slate-400">{insight.data.negative} {t('insights_negative')}</span></div>
                            <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-slate-400" /><span className="text-xs text-slate-400">{insight.data.neutral} {t('insights_neutral')}</span></div>
                          </div>
                        )}

                        {insight.type === 'focus_area' && (
                          <span className="text-sm text-gold">#{insight.data.top_tag} ({insight.data.count} {t('insights_mentions')})</span>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
